import { Link, Typography } from '@material-ui/core';
import { useState } from 'react';
import type { CockpitArtifactView } from '../model/cockpitViewModel';
import { formatCockpitDateTime } from '../model/formatters';
import { safeHttpUrl } from '../model/safeHttpUrl';
import {
  artifactAccessLabel,
  artifactRoleLabel,
  confidenceLabel,
  coverageLabel,
  lifecycleLabel,
  limitationLabel,
  pipelinePhaseLabel,
  provenanceLabel,
  humanizeCode,
} from '../model/semanticLabels';
import { useCockpitStyles } from './cockpitStyles';

interface RecordField {
  label: string;
  value: (artifact: CockpitArtifactView) => string;
}

/** The four facts a reader wants next to the document (RN-COCK-05). */
const PRIMARY_FIELDS: RecordField[] = [
  { label: 'Fase', value: a => pipelinePhaseLabel(a.phase) },
  { label: 'Skill produtora', value: a => a.skillName ?? 'Não vinculada' },
  {
    label: 'Gerado em',
    value: a =>
      a.createdAt ? formatCockpitDateTime(a.createdAt) : 'Não informado',
  },
  { label: 'Versão', value: a => a.version ?? 'Não informada' },
];

/** The rest of the observable record, mounted only on demand (RN-COCK-05). */
const TECHNICAL_FIELDS: RecordField[] = [
  { label: 'Papel', value: a => artifactRoleLabel(a.role) },
  { label: 'Confiança', value: a => confidenceLabel(a.confidence) },
  { label: 'Ciclo de vida', value: a => lifecycleLabel(a.lifecycle) },
  { label: 'Cobertura', value: a => coverageLabel(a.coverage) },
  { label: 'Acesso', value: a => artifactAccessLabel(a.access) },
  { label: 'Origem', value: a => provenanceLabel(a.provenance) },
  {
    label: 'Tipo',
    value: a =>
      a.artifactType ? humanizeCode(a.artifactType) : 'Não informado',
  },
  { label: 'Referência', value: a => a.path ?? 'Não informada' },
  { label: 'Série do documento', value: a => a.revisionKey },
  {
    label: 'Evidências vinculadas',
    value: a => String(a.evidenceIds.length),
  },
];

export const ArtifactReference = ({
  artifact,
}: {
  artifact: CockpitArtifactView;
}) => {
  const reference = artifact.path;
  if (!reference) return <>Não informada</>;
  const href = safeHttpUrl(reference);
  return href ? (
    <Link href={href} target="_blank" rel="noopener noreferrer">
      {reference}
    </Link>
  ) : (
    <>{reference}</>
  );
};

const RecordList = ({
  fields,
  artifact,
  label,
}: {
  fields: RecordField[];
  artifact: CockpitArtifactView;
  label: string;
}) => {
  const classes = useCockpitStyles();
  return (
    <dl className={classes.artifactRecordStrip} aria-label={label}>
      {fields.map(field => (
        <div key={field.label}>
          <dt>{field.label}</dt>
          <dd>
            {field.label === 'Referência' ? (
              <ArtifactReference artifact={artifact} />
            ) : (
              field.value(artifact)
            )}
          </dd>
        </div>
      ))}
    </dl>
  );
};

/** The short record read next to its authorized content. */
export const ArtifactRecordStrip = ({
  artifact,
}: {
  artifact: CockpitArtifactView;
}) => (
  <RecordList
    fields={PRIMARY_FIELDS}
    artifact={artifact}
    label="Ficha do artefato"
  />
);

export const ArtifactLimitations = ({
  artifact,
}: {
  artifact: CockpitArtifactView;
}) =>
  artifact.limitations.length > 0 ? (
    <Typography component="p" variant="caption">
      Limitações: {artifact.limitations.map(limitationLabel).join('; ')}
    </Typography>
  ) : null;

/**
 * The complete observable record and the limitations, behind a native
 * disclosure. The children are rendered only while open, so the nine extra
 * fields cost nothing to a reader who never asks for them.
 */
export const ArtifactTechnicalDetails = ({
  artifact,
}: {
  artifact: CockpitArtifactView;
}) => {
  const classes = useCockpitStyles();
  const [open, setOpen] = useState(false);
  return (
    <details
      className={classes.progressiveDetails}
      open={open}
      onToggle={event => setOpen((event.target as HTMLDetailsElement).open)}
    >
      <summary>Detalhes técnicos</summary>
      {open && (
        <>
          <RecordList
            fields={TECHNICAL_FIELDS}
            artifact={artifact}
            label="Ficha técnica do artefato"
          />
          <ArtifactLimitations artifact={artifact} />
        </>
      )}
    </details>
  );
};

export function artifactDateLabel(createdAt: string | null): string {
  return createdAt
    ? `Gerado em ${formatCockpitDateTime(createdAt)}`
    : 'Data não informada · ordem temporal não comprovada';
}
